import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Stack, Tab, Tabs, MenuItem, Select, InputLabel, FormControl,
  Chip, Grid, Alert, IconButton, Checkbox, ListItemText, OutlinedInput, FormHelperText,
  Tooltip, Snackbar
} from '@mui/material';
import {
  Add, CloudUpload, Visibility, Close, Edit, Delete, Refresh, Clear, Image as ImageIcon
} from '@mui/icons-material';
import { BASE_URL } from '../../services/configUrls';

import TextEditor from './RichTextEditor';

const getAuthHeaders = (contentType = "application/json") => {
  const token = localStorage.getItem("accessToken");
  const headers = {};
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const getDurationStatusChip = (status) => {
  switch (status) {
    case 'sent':
      return <Chip size="small" label="Sent" color="success" sx={{ fontWeight: 600, height: 22 }} />;
    case 'inactive':
      return <Chip size="small" label="Inactive" color="default" sx={{ fontWeight: 600, height: 22 }} />;
    case 'draft':
    default:
      return <Chip size="small" label="Draft" color="warning" sx={{ fontWeight: 600, height: 22 }} />;
  }
};

const menuProps = {
  PaperProps: {
    sx: {
      maxHeight: 260,
    },
  },
};

export default function ProductManagement() {
  const [tabValue, setTabValue] = useState(0);

  // Master Lists
  const [products, setProducts] = useState([]);
  const [durations, setDurations] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [roles, setRoles] = useState([]);

  // Filters for Updates
  const [filterProduct, setFilterProduct] = useState('');
  const [filterDuration, setFilterDuration] = useState('');
  const [filterRole, setFilterRole] = useState('');

  // Loaders & Notifications
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showNotification = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Fetch Functions (Only active roles)
  const fetchRoles = async () => {
    try {
      const res = await fetch(`${BASE_URL}/product-updates/roles?is_active=1`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const activeOnly = Array.isArray(data)
          ? data.filter(r => String(r.is_active) === '1' || r.is_active === 1 || r.is_active === true)
          : [];
        setRoles(activeOnly);
      }
    } catch (e) {
      console.error("Failed to fetch roles:", e);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/products/`, { headers: getAuthHeaders() });
      if (res.ok) setProducts(await res.json());
      else setError("Failed to fetch products");
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const fetchDurations = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/durations/`, { headers: getAuthHeaders() });
      if (res.ok) setDurations(await res.json());
      else setError("Failed to fetch durations");
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const fetchUpdates = async (filterOverrides = {}) => {
    setLoading(true);
    try {
      const pId = filterOverrides.product_id !== undefined ? filterOverrides.product_id : filterProduct;
      const dId = filterOverrides.duration_id !== undefined ? filterOverrides.duration_id : filterDuration;
      const rId = filterOverrides.role_id !== undefined ? filterOverrides.role_id : filterRole;

      const params = new URLSearchParams();
      params.append('limit', '100');
      if (pId) params.append('product_id', pId);
      if (dId) params.append('duration_id', dId);
      if (rId) params.append('role_id', rId);

      const res = await fetch(`${BASE_URL}/product-updates/?${params.toString()}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setUpdates(Array.isArray(data) ? data : []);
      } else {
        setError("Failed to fetch product updates");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setError("");
    if (tabValue === 0) {
      fetchProducts();
    } else if (tabValue === 1) {
      fetchUpdates();
      if (products.length === 0) fetchProducts();
      if (durations.length === 0) fetchDurations();
      if (roles.length === 0) fetchRoles();
    } else if (tabValue === 2) {
      fetchDurations();
    }
  }, [tabValue]);

  // Dialog States
  const [openProduct, setOpenProduct] = useState(false);
  const [productData, setProductData] = useState({ name: '' });

  const [openDuration, setOpenDuration] = useState(false);
  const [durationData, setDurationData] = useState({ start_date: '', end_date: '', status: 'draft' });

  // Create Update State
  const [openUpdate, setOpenUpdate] = useState(false);
  const [updateData, setUpdateData] = useState({
    product_id: '',
    duration_id: '',
    role_id: '',
    title: '',
    description: '',
    role_ids: []
  });
  const [updateFiles, setUpdateFiles] = useState([]);

  // Edit Update State
  const [openEditUpdate, setOpenEditUpdate] = useState(false);
  const [editData, setEditData] = useState({
    id: null,
    product_id: '',
    product_name: '',
    duration_id: '',
    role_id: '',
    title: '',
    description: '',
    role_ids: []
  });

  // Preview & Delete Confirmation
  const [previewUpdate, setPreviewUpdate] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, updateId: null, title: '' });

  // Product Handlers
  const handleSaveProduct = async () => {
    if (!productData.name.trim()) {
      setError("Product name is required");
      return;
    }
    try {
      const res = await fetch(`${BASE_URL}/products/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(productData)
      });
      if (!res.ok) throw new Error("Failed to save product");
      setOpenProduct(false);
      showNotification("Product created successfully!");
      fetchProducts();
    } catch (e) { setError(e.message); }
  };

  // Duration Handlers
  const handleSaveDuration = async () => {
    if (!durationData.start_date || !durationData.end_date) {
      setError("Please select both start date and end date");
      return;
    }
    try {
      const res = await fetch(`${BASE_URL}/durations/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(durationData)
      });
      if (!res.ok) throw new Error("Failed to save duration");
      setOpenDuration(false);
      showNotification("Duration window created successfully!");
      fetchDurations();
    } catch (e) { setError(e.message); }
  };

  // File Upload Handlers for Updates
  const handleFilesSelected = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setUpdateFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (index) => {
    setUpdateFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Create Update Handler
  const handleSaveUpdate = async () => {
    if (!updateData.product_id) {
      setError("Please select a target product.");
      return;
    }
    if (!updateData.duration_id) {
      setError("Please select a duration window.");
      return;
    }
    if (!updateData.title.trim()) {
      setError("Please enter an update title.");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");

      if (updateFiles.length > 0) {
        const formData = new FormData();
        formData.append('product_id', updateData.product_id);
        formData.append('duration_id', updateData.duration_id);
        formData.append('title', updateData.title.trim());
        formData.append('description', updateData.description || '');

        if (updateData.role_id) {
          formData.append('role_ids', updateData.role_id);
        }
        updateFiles.forEach(file => formData.append('files', file));

        const res = await fetch(`${BASE_URL}/product-updates/upload`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || "Failed to post update with files");
        }
      } else {
        const res = await fetch(`${BASE_URL}/product-updates/`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            product_id: parseInt(updateData.product_id),
            duration_id: parseInt(updateData.duration_id),
            title: updateData.title.trim(),
            description: updateData.description || '',
            images: [],
            role_ids: updateData.role_id ? [parseInt(updateData.role_id)] : []
          })
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || "Failed to post update");
        }
      }

      setOpenUpdate(false);
      setUpdateFiles([]);
      setUpdateData({ product_id: '', duration_id: '', role_id: '', title: '', description: '', role_ids: [] });
      showNotification("Product update posted successfully!");
      fetchUpdates();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Edit Update Handler
  const handleOpenEdit = (u) => {
    const assignedRoleId = (u.role_ids && u.role_ids.length > 0)
      ? u.role_ids[0]
      : (u.roles && u.roles.length > 0 ? u.roles[0].role_id : '');

    setEditData({
      id: u.id,
      product_id: u.product_id,
      product_name: u.product_name || (products.find(p => p.id === u.product_id)?.name) || '',
      duration_id: u.duration_id || (u.duration?.id || ''),
      role_id: assignedRoleId || '',
      title: u.title || '',
      description: u.description || '',
      role_ids: assignedRoleId ? [parseInt(assignedRoleId)] : []
    });
    setOpenEditUpdate(true);
  };

  const handleSaveEditUpdate = async () => {
    if (!editData.title.trim()) {
      setError("Update title cannot be empty.");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        duration_id: editData.duration_id ? parseInt(editData.duration_id) : undefined,
        title: editData.title.trim(),
        description: editData.description || '',
        role_ids: editData.role_id ? [parseInt(editData.role_id)] : []
      };
      const res = await fetch(`${BASE_URL}/product-updates/${editData.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to edit product update");
      }
      setOpenEditUpdate(false);
      showNotification("Product update saved successfully!");
      fetchUpdates();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete Update Handler
  const handleConfirmDelete = async () => {
    if (!deleteConfirm.updateId) return;
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/product-updates/${deleteConfirm.updateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to delete update");
      }
      setDeleteConfirm({ open: false, updateId: null, title: '' });
      showNotification("Product update deleted successfully!");
      fetchUpdates();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: "background.default", minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight={700} color="primary.main" mb={3} letterSpacing={-0.5}>
        Product Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tabValue}
        onChange={(e, v) => setTabValue(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'primary.light' }}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab label="Products" sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem' }} />
        <Tab label="Product Updates" sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem' }} />
        <Tab label="Durations" sx={{ fontWeight: 600, textTransform: 'none', fontSize: '1rem' }} />
      </Tabs>

      {/* PRODUCTS TAB */}
      {tabValue === 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'primary.light', boxShadow: '0 4px 12px rgba(25, 118, 210, 0.05)' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={700} color="primary.dark">Products</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                disableElevation
                sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' }, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
                onClick={() => { setProductData({ name: '' }); setOpenProduct(true); }}
              >
                Add Product
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#e3f2fd' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Images Count</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Updates Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}><CircularProgress size={24} color="primary" /></TableCell></TableRow>
                  ) : products.length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.disabled' }}>No products found.</TableCell></TableRow>
                  ) : products.map(p => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.id}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{p.name}</TableCell>
                      <TableCell><Chip size="small" label={p.images_count || 0} sx={{ bgcolor: '#bbdefb', color: '#0d47a1', fontWeight: 600 }} /></TableCell>
                      <TableCell><Chip size="small" label={p.updates_count || 0} sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* PRODUCT UPDATES TAB */}
      {tabValue === 1 && (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'primary.light', boxShadow: '0 4px 12px rgba(25, 118, 210, 0.05)' }}>
          <CardContent>
            {/* Top Toolbar */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Typography variant="h6" fontWeight={700} color="primary.dark">Product Updates</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                disableElevation
                sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' }, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
                onClick={() => {
                  setUpdateData({ product_id: '', duration_id: '', role_id: '', title: '', description: '', role_ids: [] });
                  setUpdateFiles([]);
                  setOpenUpdate(true);
                }}
              >
                Post Update
              </Button>
            </Box>

            {/* Filter Bar */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2.5, bgcolor: '#f8fafc', borderColor: '#e2e8f0', borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter Product</InputLabel>
                    <Select
                      value={filterProduct}
                      label="Filter Product"
                      onChange={(e) => {
                        setFilterProduct(e.target.value);
                        fetchUpdates({ product_id: e.target.value });
                      }}
                      MenuProps={menuProps}
                    >
                      <MenuItem value=""><em>All Products</em></MenuItem>
                      {products.map(p => (
                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter Duration</InputLabel>
                    <Select
                      value={filterDuration}
                      label="Filter Duration"
                      onChange={(e) => {
                        setFilterDuration(e.target.value);
                        fetchUpdates({ duration_id: e.target.value });
                      }}
                      MenuProps={menuProps}
                    >
                      <MenuItem value=""><em>All Durations</em></MenuItem>
                      {durations.map(d => (
                        <MenuItem key={d.id} value={d.id}>
                          {formatDate(d.start_date)} - {formatDate(d.end_date)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Filter Visible Role</InputLabel>
                    <Select
                      value={filterRole}
                      label="Filter Visible Role"
                      onChange={(e) => {
                        setFilterRole(e.target.value);
                        fetchUpdates({ role_id: e.target.value });
                      }}
                      MenuProps={menuProps}
                    >
                      <MenuItem value=""><em>All Roles</em></MenuItem>
                      {roles.map(r => (
                        <MenuItem key={r.role_id} value={r.role_id}>
                          {r.role_name || r.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3} display="flex" gap={1} alignItems="center" justifyContent="flex-end">
                  {(filterProduct || filterDuration || filterRole) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<Clear />}
                      onClick={() => {
                        setFilterProduct('');
                        setFilterDuration('');
                        setFilterRole('');
                        fetchUpdates({ product_id: '', duration_id: '', role_id: '' });
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Reset
                    </Button>
                  )}
                  <Tooltip title="Refresh updates">
                    <IconButton
                      size="small"
                      onClick={() => fetchUpdates()}
                      sx={{ color: '#1976d2', bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Paper>

            {/* Updates Table */}
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#e3f2fd' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Duration Window</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Visible to Roles</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Images</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} color="primary" /></TableCell></TableRow>
                  ) : updates.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.disabled' }}>No updates found.</TableCell></TableRow>
                  ) : updates.map(u => (
                    <TableRow key={u.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{u.id}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {u.product_name || (products.find(p => p.id === u.product_id)?.name) || `#${u.product_id}`}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.title}
                      </TableCell>
                      <TableCell>
                        {u.duration ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                              {formatDate(u.duration.start_date)} - {formatDate(u.duration.end_date)}
                            </Typography>
                            {getDurationStatusChip(u.duration.status)}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.roles && u.roles.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 220 }}>
                            {u.roles.map(r => (
                              <Chip
                                key={r.role_id}
                                size="small"
                                label={r.role_name || r.name || `Role #${r.role_id}`}
                                sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.72rem' }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Chip
                            size="small"
                            label="All Roles (Public)"
                            variant="outlined"
                            color="success"
                            sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={u.images?.length || 0}
                          sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                        {formatDate(u.created_at)}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Preview update">
                            <IconButton
                              size="small"
                              sx={{ color: '#1976d2', bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
                              onClick={() => setPreviewUpdate(u)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit update">
                            <IconButton
                              size="small"
                              sx={{ color: '#ed6c02', bgcolor: '#fff3e0', '&:hover': { bgcolor: '#ffe0b2' } }}
                              onClick={() => handleOpenEdit(u)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete update">
                            <IconButton
                              size="small"
                              sx={{ color: '#d32f2f', bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}
                              onClick={() => setDeleteConfirm({ open: true, updateId: u.id, title: u.title })}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* DURATIONS TAB */}
      {tabValue === 2 && (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'primary.light', boxShadow: '0 4px 12px rgba(25, 118, 210, 0.05)' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={700} color="primary.dark">Duration Windows</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                disableElevation
                sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' }, textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}
                onClick={() => { setDurationData({ start_date: '', end_date: '', status: 'draft' }); setOpenDuration(true); }}
              >
                Add Duration Window
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#e3f2fd' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Start Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>End Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark', py: 1.5 }}>Created Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={24} color="primary" /></TableCell></TableRow>
                  ) : durations.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.disabled' }}>No duration windows found.</TableCell></TableRow>
                  ) : durations.map(d => (
                    <TableRow key={d.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{d.id}</TableCell>
                      <TableCell>{formatDate(d.start_date)}</TableCell>
                      <TableCell>{formatDate(d.end_date)}</TableCell>
                      <TableCell>{getDurationStatusChip(d.status)}</TableCell>
                      <TableCell>{formatDate(d.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* DIALOGS */}

      {/* 1. Add Product Dialog */}
      <Dialog open={openProduct} onClose={() => setOpenProduct(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Add New Product</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            label="Product Name"
            placeholder="e.g. Virtual Internship Platform"
            value={productData.name}
            onChange={e => setProductData({ ...productData, name: e.target.value })}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setOpenProduct(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveProduct}
            disableElevation
            sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' }, textTransform: 'none' }}
          >
            Save Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2. Add Duration Dialog */}
      <Dialog open={openDuration} onClose={() => setOpenDuration(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Duration Window</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              type="date"
              size="small"
              fullWidth
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={durationData.start_date}
              onChange={e => setDurationData({ ...durationData, start_date: e.target.value })}
            />
            <TextField
              type="date"
              size="small"
              fullWidth
              label="End Date"
              InputLabelProps={{ shrink: true }}
              value={durationData.end_date}
              onChange={e => setDurationData({ ...durationData, end_date: e.target.value })}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={durationData.status}
                label="Status"
                onChange={e => setDurationData({ ...durationData, status: e.target.value })}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setOpenDuration(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveDuration}
            disableElevation
            sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' }, textTransform: 'none' }}
          >
            Save Duration
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3. Post Product Update Dialog */}
      <Dialog open={openUpdate} onClose={() => setOpenUpdate(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Post Product Update</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            {/* Target Product */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Target Product *</InputLabel>
                <Select
                  value={updateData.product_id}
                  label="Target Product *"
                  onChange={e => setUpdateData({ ...updateData, product_id: e.target.value })}
                  MenuProps={menuProps}
                >
                  {products.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Duration Window */}
            <Grid item xs={12} sm={4}>
              <Box display="flex" gap={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Duration Window *</InputLabel>
                  <Select
                    value={updateData.duration_id}
                    label="Duration Window *"
                    onChange={e => setUpdateData({ ...updateData, duration_id: e.target.value })}
                    MenuProps={menuProps}
                  >
                    {durations.map(d => (
                      <MenuItem key={d.id} value={d.id}>
                        {formatDate(d.start_date)} to {formatDate(d.end_date)} ({d.status})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Add new duration window">
                  <Button
                    variant="outlined"
                    sx={{ minWidth: '40px', p: '6px', color: '#1976d2', borderColor: '#1976d2', '&:hover': { borderColor: '#1565c0', bgcolor: '#e3f2fd' } }}
                    onClick={() => { setDurationData({ start_date: '', end_date: '', status: 'draft' }); setOpenDuration(true); }}
                  >
                    <Add />
                  </Button>
                </Tooltip>
              </Box>
            </Grid>

            {/* Target Role (Small single dropdown) */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Visible to Role</InputLabel>
                <Select
                  value={updateData.role_id || ''}
                  label="Visible to Role"
                  onChange={e => {
                    const val = e.target.value;
                    setUpdateData({
                      ...updateData,
                      role_id: val,
                      role_ids: val ? [parseInt(val)] : []
                    });
                  }}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">
                    <em>All Roles (Public)</em>
                  </MenuItem>
                  {roles.map(r => (
                    <MenuItem key={r.role_id} value={r.role_id}>
                      {r.role_name || r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Update Title *"
                placeholder="e.g. Added new dashboard metrics and role permissions"
                value={updateData.title}
                onChange={e => setUpdateData({ ...updateData, title: e.target.value })}
              />
            </Grid>

            {/* Rich Text Description */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" mb={1} color="text.secondary" fontWeight={600}>
                Update Description (Rich Text & Inline Images)
              </Typography>
              <Box border={1} borderColor="grey.300" borderRadius={1} p={1} bgcolor="#fff">
                <TextEditor
                  content={updateData.description}
                  onContentChange={(val) => setUpdateData({ ...updateData, description: val })}
                />
              </Box>
            </Grid>

            {/* Attached Files (Multipart upload support) */}
            <Grid item xs={12}>
              <Box sx={{ border: '1px dashed #90caf9', borderRadius: 2, p: 2, bgcolor: '#fbfcfe' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} color="primary.dark">
                      Attach Additional Images (Optional)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      You can paste screenshots directly into the rich text editor above, or upload images here.
                    </Typography>
                  </Box>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    Select Images
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handleFilesSelected}
                    />
                  </Button>
                </Box>
                {updateFiles.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                    {updateFiles.map((file, idx) => (
                      <Chip
                        key={idx}
                        icon={<ImageIcon fontSize="small" />}
                        label={`${file.name} (${Math.round(file.size / 1024)} KB)`}
                        onDelete={() => handleRemoveFile(idx)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setOpenUpdate(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveUpdate}
            disabled={loading}
            disableElevation
            sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' }, textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Post Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 4. Edit Product Update Dialog */}
      <Dialog open={openEditUpdate} onClose={() => setOpenEditUpdate(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Product Update</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            {/* Target Product (Read-only) */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Product"
                value={editData.product_name}
                disabled
              />
            </Grid>

            {/* Duration Window */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Duration Window</InputLabel>
                <Select
                  value={editData.duration_id}
                  label="Duration Window"
                  onChange={e => setEditData({ ...editData, duration_id: e.target.value })}
                  MenuProps={menuProps}
                >
                  {durations.map(d => (
                    <MenuItem key={d.id} value={d.id}>
                      {formatDate(d.start_date)} to {formatDate(d.end_date)} ({d.status})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Target Role (Small single dropdown) */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Visible to Role</InputLabel>
                <Select
                  value={editData.role_id || ''}
                  label="Visible to Role"
                  onChange={e => {
                    const val = e.target.value;
                    setEditData({
                      ...editData,
                      role_id: val,
                      role_ids: val ? [parseInt(val)] : []
                    });
                  }}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">
                    <em>All Roles (Public)</em>
                  </MenuItem>
                  {roles.map(r => (
                    <MenuItem key={r.role_id} value={r.role_id}>
                      {r.role_name || r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Update Title *"
                value={editData.title}
                onChange={e => setEditData({ ...editData, title: e.target.value })}
              />
            </Grid>

            {/* Rich Text Description */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" mb={1} color="text.secondary" fontWeight={600}>
                Update Description (Rich Text)
              </Typography>
              <Box border={1} borderColor="grey.300" borderRadius={1} p={1} bgcolor="#fff">
                <TextEditor
                  content={editData.description}
                  onContentChange={(val) => setEditData({ ...editData, description: val })}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={() => setOpenEditUpdate(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEditUpdate}
            disabled={loading}
            disableElevation
            sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' }, textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 5. Preview Dialog */}
      <Dialog open={!!previewUpdate} onClose={() => setPreviewUpdate(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {previewUpdate && (
          <>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={700}>{previewUpdate.title}</Typography>
              <IconButton onClick={() => setPreviewUpdate(null)} size="small"><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {/* Meta details */}
              <Box sx={{ mb: 2.5, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Product</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {previewUpdate.product_name || (products.find(p => p.id === previewUpdate.product_id)?.name) || `#${previewUpdate.product_id}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Duration Window</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {previewUpdate.duration ? (
                        `${formatDate(previewUpdate.duration.start_date)} - ${formatDate(previewUpdate.duration.end_date)}`
                      ) : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Created Date</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDate(previewUpdate.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Role Visibility
                    </Typography>
                    {previewUpdate.roles && previewUpdate.roles.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {previewUpdate.roles.map(r => (
                          <Chip
                            key={r.role_id}
                            size="small"
                            label={r.role_name || r.name}
                            sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Chip size="small" label="All Roles (Public)" variant="outlined" color="success" sx={{ fontWeight: 600 }} />
                    )}
                  </Grid>
                </Grid>
              </Box>

              {/* Description Body */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>Content</Typography>
                {previewUpdate.description ? (
                  <Box
                    className="tiptap-content"
                    dangerouslySetInnerHTML={{ __html: previewUpdate.description }}
                    sx={{
                      '& img': { maxWidth: '100%', borderRadius: 1.5 },
                      p: 2,
                      bgcolor: '#fff',
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                      lineHeight: 1.6
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.disabled">No description provided.</Typography>
                )}
              </Box>

              {/* Images Grid */}
              {previewUpdate.images && previewUpdate.images.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Attached Images ({previewUpdate.images.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {previewUpdate.images.map((img, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <Box
                          component="a"
                          href={img.presigned_url || img.object_key}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ display: 'block', textDecoration: 'none' }}
                        >
                          <Box
                            component="img"
                            src={img.presigned_url}
                            alt={`attachment-${idx}`}
                            width="100%"
                            borderRadius={2}
                            sx={{
                              objectFit: 'cover',
                              aspectRatio: '4/3',
                              border: '1px solid #e2e8f0',
                              transition: 'transform 0.2s',
                              '&:hover': { transform: 'scale(1.02)' }
                            }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* 6. Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, updateId: null, title: '' })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
          Delete Product Update?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete <strong>"{deleteConfirm.title}"</strong>? This will also remove any role associations and images attached to this update.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setDeleteConfirm({ open: false, updateId: null, title: '' })}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={loading}
            disableElevation
            sx={{ textTransform: 'none' }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', boxShadow: 3, borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
